import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Calendar, MapPin, PartyPopper, Search } from "lucide-react";
import { getEventHistory } from "../../services/historyService";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import Badge from "../../components/common/Badge";
import Pagination from "../../components/common/Pagination";
import { color, radius, shadow } from "../../styles/theme";

const PAGE_SIZE = 20;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "");

/** Single date if the event starts/ends the same calendar day, otherwise a "start – end" range. */
const formatDateRange = (start, end) => {
  if (!start) return "";
  if (!end) return formatDate(start);
  const sameDay = new Date(start).toDateString() === new Date(end).toDateString();
  return sameDay ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
};

const EVENT_TYPE_VARIANT = { Public: "primary", Private: "neutral", FamilyEvent: "warning", ARoom: "info" };
const EVENT_TYPE_LABEL = { FamilyEvent: "Family Event", ARoom: "@Room" };
const eventTypeLabel = (type) => EVENT_TYPE_LABEL[type] || type;

const ORIGIN_FILTERS = ["all", "public", "nonPublic"];

/**
 * Event History — ported from mobile's event-history screen
 * (src/Screens/event-history), extended with the extra fields
 * `GetMyEventHistory` (EventsController.cs) already returns but the original
 * port never surfaced: EndDateTime, Location, EventType. Cards are clickable
 * through to Event Details here (a small deliberate improvement — mobile
 * disables navigation on history cards for no apparent reason;
 * EventDetails.jsx already renders any event this promoter administers
 * regardless of status), landing on its Summary tab first since a history
 * lookup is almost always "how did this event do" rather than "let me edit
 * it". Fixed along the way: the click handler keyed off `event.Id`, a field
 * this endpoint's `EventHistoryDto` doesn't have — it's `OriginalEventId` —
 * so clicking a card silently did nothing.
 */
const EventHistory = () => {
  const { t } = useTranslation(["history", "common"]);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [originFilter, setOriginFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search box so it doesn't hit the (now server-side) search
  // endpoint on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = (pageNumber = 1, searchTerm = debouncedSearch) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    getEventHistory({ pageNumber, pageSize: PAGE_SIZE, search: searchTerm })
      .then((res) => {
        setEvents(res.data?.Result || []);
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("toast.loadFailed")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  // Re-fetch page 1 whenever the (debounced) search term changes — this also
  // covers the initial load, since debouncedSearch starts at "".
  useEffect(() => {
    load(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // The filter only runs over the current page's events — with real
  // pagination now in place (see Pagination below) there's no larger
  // in-memory set to filter across; a narrow filter may need paging through
  // to find matches on other pages.
  const filteredEvents = events.filter((event) => {
    if (originFilter === "public") return event.Origin === "Public";
    if (originFilter === "nonPublic") return event.Origin && event.Origin !== "Public";
    return true;
  });

  if (loading) return <Loader />;

  return (
    <div>
      <h2 style={s.title}>{t("title")}</h2>

      <div style={s.searchRow}>
        <Search size={15} strokeWidth={2.25} style={s.searchIcon} />
        <input
          type="text"
          className="input"
          style={s.searchInput}
          placeholder={t("searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {(events.length > 0 || debouncedSearch) && (
        <div style={s.filterRow}>
          <span style={s.filterLabel}>{t("originFilter.label")}</span>
          {ORIGIN_FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              style={{ ...s.filterChip, ...(originFilter === key ? s.filterChipActive : {}) }}
              onClick={() => setOriginFilter(key)}
            >
              {t(`originFilter.options.${key}`)}
            </button>
          ))}
        </div>
      )}

      {events.length === 0 ? (
        <EmptyState icon={PartyPopper} text={debouncedSearch ? t("noMatch") : t("empty")} />
      ) : filteredEvents.length === 0 ? (
        <EmptyState icon={PartyPopper} text={t("noMatch")} />
      ) : (
        <div style={s.grid}>
          {filteredEvents.map((event, i) => (
            <div
              key={event.OriginalEventId ?? i}
              className="icon-tile"
              style={s.card}
              onClick={() => event.OriginalEventId && navigate(`/events/${event.OriginalEventId}`, { state: { initialTab: "Summary", from: "history" } })}
            >
              <div style={s.thumbnailWrap}>
                {event.Thumbnail ? (
                  <img src={String(event.Thumbnail).replace("\\", "/")} alt="" style={s.thumbnail} />
                ) : (
                  <div style={s.thumbnailPlaceholder}><PartyPopper size={26} strokeWidth={1.75} color={color.primary400} /></div>
                )}
                {event.EventType && (
                  <span style={s.typeBadge}>
                    <Badge variant={EVENT_TYPE_VARIANT[event.EventType] || "neutral"}>{eventTypeLabel(event.EventType)}</Badge>
                  </span>
                )}
              </div>
              <div style={s.cardBody}>
                <div style={s.name}>{event.EventName || event.Name}</div>
                {event.StartDateTime && (
                  <div style={s.metaRow}><Calendar size={11} strokeWidth={2} /> {formatDateRange(event.StartDateTime, event.EndDateTime)}</div>
                )}
                {event.Location && (
                  <div style={s.metaRow} title={event.Location}><MapPin size={11} strokeWidth={2} /> <span style={s.metaTruncate}>{event.Location}</span></div>
                )}
                {event.PromoterName && <div style={s.promoter}>{event.PromoterName}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {events.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={meta?.TotalCount ?? meta?.totalCount ?? 0}
          onPageChange={(p) => load(p)}
          disabled={loadingMore}
        />
      )}
    </div>
  );
};

const s = {
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, marginBottom: 20, letterSpacing: -0.3 },
  searchRow: { position: "relative", maxWidth: 340, marginBottom: 16 },
  searchIcon: { position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)", color: color.textMuted, pointerEvents: "none" },
  searchInput: { width: "100%", boxSizing: "border-box", paddingLeft: 36 },
  filterRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" },
  filterLabel: { fontSize: 12.5, fontWeight: 600, color: color.textSecondary, marginRight: 4 },
  filterChip: {
    padding: "6px 14px", borderRadius: radius.pill, border: `1.5px solid ${color.border}`,
    background: color.gray50, color: color.gray700, fontWeight: 600, fontSize: 12.5, cursor: "pointer",
  },
  filterChipActive: { border: `1.5px solid ${color.primary500}`, background: color.primary50, color: color.primary600 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 },
  card: { background: color.surface, borderRadius: radius.lg, overflow: "hidden", cursor: "pointer", boxShadow: shadow.sm, border: `1px solid ${color.border}` },
  thumbnailWrap: { position: "relative" },
  thumbnail: { width: "100%", height: 120, objectFit: "cover", display: "block" },
  thumbnailPlaceholder: { width: "100%", height: 120, display: "flex", alignItems: "center", justifyContent: "center", background: color.primary50 },
  typeBadge: { position: "absolute", top: 8, left: 8 },
  cardBody: { padding: "12px 14px" },
  name: { fontWeight: 700, fontSize: 14, color: color.textPrimary, marginBottom: 7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  metaRow: { display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: color.textSecondary, marginBottom: 4 },
  metaTruncate: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  promoter: { fontSize: 11.5, color: color.textMuted, marginTop: 2 },
};

export default EventHistory;
